import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  IndexTable,
  Badge,
  useBreakpoints,
  useIndexResourceState,
  Text,
  Button,
  Modal,
} from '@shopify/polaris';
import useAxiosClient from '../axiosClient';
import { useTranslation } from 'react-i18next';
import '../../i18n';

const PolicyManagement = () => {
  const axiosClient = useAxiosClient();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  // States
  const [policies, setPolicies] = useState([]);
  const [activeDeleteModal, setActiveDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState(null);

  // Fetch policy data
  useEffect(() => {
    const fetchPolicies = async () => {
      try {
        const response = await axiosClient.get('/getPolicies');
        if (response.data.success) {
          setPolicies(response.data.policies);
        }
      } catch (error) {
        // Error handled silently
      }
    };
    fetchPolicies();
  }, []);

  // Fetch single policy data
  const fetchPolicyDetails = async (id) => {
    try {
      const response = await axiosClient.get(`/policies/${id}`);
      if (response.data.success) {
        setSelectedPolicy(response.data.policy);
      }
    } catch (error) {
      // Error handled silently
    }
  };

  const resourceName = {
    singular: t('policy'),
    plural: t('policies'),
  };

  const {
    selectedResources,
    allResourcesSelected,
    handleSelectionChange,
    clearSelection,
  } = useIndexResourceState(policies, {
    resourceIDResolver: (policy) => policy.id?.toString() || '',
  });

  // Bulk delete action: open the delete confirmation modal.
  const handleBulkDelete = useCallback(() => {
    if (selectedResources.length === 0) {
      alert(t('please_select_at_least_one_policy_to_delete'));
      return;
    }
    setActiveDeleteModal(true);
  }, [selectedResources]);

  const promotedBulkActions = [
    {
      content: t('delete_policy'),
      onAction: handleBulkDelete,
    },
  ];

  // Update the handleConfirmDelete function
  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      const response = await axiosClient.post('/deletePolicies', {
        data: { ids: selectedResources }
      });

      if (response.data.success) {
        // Remove deleted policies from state
        setPolicies(prevPolicies =>
          prevPolicies.filter(policy => !selectedResources.includes(policy.id.toString()))
        );

        // Clear selection
        clearSelection();

        // Show success message
        shopify.toast.show(t('policies_deleted'));
      } else {
        // Show error message
        shopify.toast.show(response.data.message || t('failed_to_delete_policies'), { error: true });
      }
    } catch (error) {
      shopify.toast.show(error.response?.data?.message || t('error_deleting_policies'), { error: true });
    } finally {
      setIsDeleting(false);
      setActiveDeleteModal(false);
    }
  };
  

  return (
    <Page 
    title={t('policy_management')} 
    fullWidth
    backAction={{
      content: t('back'),
      onAction: () => navigate('/TimeOff')
    }}
  >
      <Layout>
        <Layout.Section>
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '1rem' }}>
            <Button variant="primary" onClick={() => navigate('/TimeOff/Policy/CreatePolicy')}>
              {t('create_policy')}
            </Button>
          </div>

          <Card>
            <IndexTable
              resourceName={resourceName}
              itemCount={policies.length}
              selectedItemsCount={allResourcesSelected ? t('all') : selectedResources.length}
              onSelectionChange={handleSelectionChange}
              headings={[
                { title: t('policy_name') },
                { title: t('compensation') },
                { title: t('full_day_duration') },
                { title: t('policy_type') },
                { title: t('status') },
              ]}
              selectable
              promotedBulkActions={promotedBulkActions}
            >
              {policies.map((policy, index) => {
                const isDisabled = policy.status === 'Inactive';

                return (
                  <IndexTable.Row
                    id={policy.id?.toString() || index.toString()}
                    key={policy.id?.toString() || index.toString()}
                    selected={!isDisabled && selectedResources.includes(policy.id?.toString())}
                    position={index}
                    onClick={!isDisabled ? () => {
                      fetchPolicyDetails(policy.id);
                      navigate(`/TimeOff/Policy/PolicyDetails/${policy.id}`);
                    } : undefined} // Disable onClick if Inactive
                    style={{
                      cursor: isDisabled ? 'not-allowed' : 'pointer', // Change cursor to indicate disabled state
                      opacity: isDisabled ? 0.5 : 1, // Dim the row for disabled state
                      pointerEvents: isDisabled ? 'none' : 'auto', // Disable all pointer events for the row
                    }}
                    disabled={isDisabled}
                  >
                    <IndexTable.Cell>{policy.policy_name || t('n_a')}</IndexTable.Cell>
                    <IndexTable.Cell>{policy.paid_status || t('n_a')}</IndexTable.Cell>
                    <IndexTable.Cell>{policy.full_day_duration || t('n_a')}</IndexTable.Cell>
                    <IndexTable.Cell>{policy.policy_type || t('n_a')}</IndexTable.Cell>
                    <IndexTable.Cell>
                      <Badge tone={policy.status === 'Active' ? 'success' : 'warning'}>
                        {policy.status}
                      </Badge>
                    </IndexTable.Cell>
                  </IndexTable.Row>
                );
              })}
            </IndexTable>
          </Card>

          {/* Delete Confirmation Modal */}
          <Modal
            open={activeDeleteModal}
            onClose={() => setActiveDeleteModal(false)}
            title={t('delete_policy')}
            primaryAction={{
              content: t('delete'),
              destructive: true,
              onAction: handleConfirmDelete,
              loading: isDeleting,
            }}
            secondaryActions={[
              {
                content: t('cancel'),
                onAction: () => setActiveDeleteModal(false),
              },
            ]}
          >
            <Modal.Section>
              <Text variant="bodyMd">
                {t('are_you_sure_you_want_to_delete_selected_policies')}
              </Text>
            </Modal.Section>
          </Modal>
        </Layout.Section>
      </Layout>
    </Page>
  );
};

export default PolicyManagement;
